import Icon from '@atoms/Icon/Icon';
import { ISharePanel } from '@models/ISharePanel';
import IconSizeEnum from '@models/enums/IconSizeEnum';
import InfoTypeEnum from '@models/enums/InfoTypeEnum';
import InfoModal from '@molecules/InfoModal/InfoModal';
import useAriaLabels from '@utils/hooks/useAriaLabels';
import useClientPathname from '@utils/hooks/useClientPathname';
import { useEffect, useState } from 'react';

interface Props extends ISharePanel {
  readonly dataTestId?: string;
  readonly hasWhatsapp?: boolean;
}

const SharePanel = ({
  title,
  tooltipText = '',
  hasWhatsapp = false,
  shareOrigin = 'article_share',
  dataTestId = 'Share-Panel',
}: Props) => {
  const [isCopySuccessModalOpen, setIsCopySuccessModalOpen] = useState(false);
  const [pageUrl, setPageUrl] = useState('');
  const pathname = useClientPathname();
  const {
    copyToClipboardAriaLabel,
    shareToFacebookAriaLabel,
    shareToXAriaLabel,
    shareToWhatsappAriaLabel,
  } = useAriaLabels();

  const shareUrl = (source: string) =>
    `${pageUrl}?utm_source=${source}&utm_medium=${shareOrigin}`;

  const socialShareLinks = {
    facebook: `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(
      shareUrl('facebook')
    )}`,
    twitter: `https://twitter.com/intent/tweet?url=${encodeURIComponent(
      shareUrl('twitter')
    )}`,
    whatsapp: `whatsapp://send?text=${encodeURIComponent(
      shareUrl('whatsapp')
    )}`,
  };

  const copyToClipboard = async () => {
    try {
      await navigator.clipboard.writeText(shareUrl('url'));
      setIsCopySuccessModalOpen((isOpen) => !isOpen);
    } catch (error) {
      // biome-ignore lint/suspicious/noConsole: Allowed during error reporting
      console.log({ error });
    }
  };

  useEffect(() => {
    setPageUrl(window.location.origin + pathname);
  }, [pathname]);

  return (
    <>
      <div className="text-black-100 dark:text-white" data-testid={dataTestId}>
        {title && (
          <p className="body-two" data-testid={`${dataTestId}-Title`}>
            {title}
          </p>
        )}

        <div className="mt-4 flex gap-5">
          <button
            type="button"
            onClick={() => copyToClipboard()}
            aria-label={copyToClipboardAriaLabel}
            data-testid={`${dataTestId}-Copy-To-Clipboard-Button`}
          >
            <Icon
              name="share"
              className="text-black-100 transition duration-300 ease-in-out hover:text-grey-450 dark:text-white dark:hover:text-yellow"
              size={IconSizeEnum.lg}
            />
          </button>

          <a
            href={socialShareLinks.facebook}
            target="_blank"
            rel="noreferrer"
            aria-label={shareToFacebookAriaLabel}
            data-testid={`${dataTestId}-Facebook-Link`}
          >
            <Icon
              name="facebook"
              className="text-black-100 transition duration-300 ease-in-out hover:text-grey-450 dark:text-white dark:hover:text-yellow"
              size={IconSizeEnum.lg}
            />
          </a>

          <a
            href={socialShareLinks.twitter}
            target="_blank"
            rel="noreferrer"
            aria-label={shareToXAriaLabel}
            data-testid={`${dataTestId}-Twitter-Link`}
          >
            <Icon
              name="twitter"
              className="text-black-100 transition duration-300 ease-in-out hover:text-grey-450 dark:text-white dark:hover:text-yellow"
              size={IconSizeEnum.lg}
            />
          </a>

          {hasWhatsapp && (
            <a
              href={socialShareLinks.whatsapp}
              target="_blank"
              rel="noreferrer"
              aria-label={shareToWhatsappAriaLabel}
              data-testid={`${dataTestId}-Whatsapp-Link`}
            >
              <Icon
                name="whatsapp"
                className="text-black-100 transition duration-300 ease-in-out hover:text-grey-450 dark:text-white dark:hover:text-yellow"
                size={IconSizeEnum.lg}
              />
            </a>
          )}
        </div>
      </div>

      <InfoModal
        type={InfoTypeEnum.Success}
        isModalOpen={isCopySuccessModalOpen}
        toggleModal={setIsCopySuccessModalOpen}
        title={tooltipText}
        dataTestId={`${dataTestId}-Info-Modal`}
      />
    </>
  );
};

export default SharePanel;
